import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  PreconditionCondition,
  PreconditionExpr,
  PreconditionFieldDef,
  PreconditionOp,
  PreconditionRuleConditional,
} from "@/services/platformEnterprisesService";

import { PreconditionExprInput } from "./precondition-expr-input";

interface Props {
  rule: PreconditionRuleConditional;
  fields: PreconditionFieldDef[];
  onChange: (next: PreconditionRuleConditional) => void;
  onRemove: () => void;
}

const OPERATORS: PreconditionOp[] = [
  "eq",
  "neq",
  "gt",
  "lt",
  "gte",
  "lte",
  "between",
  "in",
  "contains",
];

function emptyCondition(): PreconditionCondition {
  return {
    left: { kind: "field", key: "" },
    op: "gte",
    right: { kind: "const", value: 0 },
  };
}

export function PreconditionRuleConditionalEditor({
  rule,
  fields,
  onChange,
  onRemove,
}: Props): JSX.Element {
  const { t } = useTranslation();

  function updateCondition(idx: number, patch: Partial<PreconditionCondition>): void {
    onChange({
      ...rule,
      conditions: rule.conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    });
  }

  function changeOp(idx: number, op: PreconditionOp): void {
    const current = rule.conditions[idx];
    const next: PreconditionCondition = { ...current, op };
    if (op === "between" && !next.rightMax) {
      next.rightMax = { kind: "const", value: 0 };
    }
    if (op !== "between") {
      delete next.rightMax;
    }
    if (op === "in" && !next.rightList) {
      next.rightList = [{ kind: "const", value: "" }];
    }
    if (op !== "in") {
      delete next.rightList;
    }
    updateCondition(idx, next);
  }

  function addCondition(): void {
    onChange({
      ...rule,
      conditions: [
        ...rule.conditions,
        { ...emptyCondition(), combinator: "AND" },
      ],
    });
  }

  function removeCondition(idx: number): void {
    onChange({
      ...rule,
      conditions: rule.conditions.filter((_, i) => i !== idx),
    });
  }

  return (
    <div className="space-y-3 rounded-md border bg-muted/30 p-3">
      <div className="flex items-center justify-between gap-2">
        <Input
          value={rule.description ?? ""}
          onChange={(e) => onChange({ ...rule, description: e.target.value })}
          placeholder={t("enterpriseProductBuilder.ruleDescriptionPlaceholder")}
          className="h-8 text-xs"
        />
        <Button
          size="sm"
          variant="ghost-destructive"
          className="h-7 shrink-0"
          onClick={onRemove}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>

      {rule.conditions.length === 0 && (
        <p className="rounded-md border border-dashed py-3 text-center text-xs text-muted-foreground">
          {t("enterpriseProductBuilder.noConditionsYet")}
        </p>
      )}

      {rule.conditions.map((cond, idx) => {
        const isNumericOp = cond.op !== "contains";
        return (
          <div key={idx} className="space-y-2 rounded-md border bg-background p-2">
            {idx > 0 && (
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground">
                  {t("enterpriseProductBuilder.combinator")}
                </Label>
                <Select
                  value={cond.combinator ?? "AND"}
                  onValueChange={(v) =>
                    updateCondition(idx, { combinator: v as "AND" | "OR" })
                  }
                >
                  <SelectTrigger className="h-7 w-[80px] text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AND">
                      {t("enterpriseProductBuilder.combinatorAnd")}
                    </SelectItem>
                    <SelectItem value="OR">
                      {t("enterpriseProductBuilder.combinatorOr")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                {t("enterpriseProductBuilder.conditionLeft")}
              </Label>
              <PreconditionExprInput
                expr={cond.left}
                fields={fields}
                numericOnly={isNumericOp}
                onChange={(next) => updateCondition(idx, { left: next })}
              />
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">
                {t("enterpriseProductBuilder.operator")}
              </Label>
              <Select value={cond.op} onValueChange={(v) => changeOp(idx, v as PreconditionOp)}>
                <SelectTrigger className="h-8 w-[140px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OPERATORS.map((op) => (
                    <SelectItem key={op} value={op}>
                      {t(`enterpriseProductBuilder.op.${op}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                {cond.op === "between"
                  ? t("enterpriseProductBuilder.conditionRightMin")
                  : t("enterpriseProductBuilder.conditionRight")}
              </Label>
              {(() => {
                const leftField = cond.left.kind === "field"
                  ? fields.find((f) => f.key === (cond.left as { kind: "field"; key: string }).key)
                  : undefined;
                const rightSelectOpts =
                  leftField?.type === "select" &&
                  leftField.options?.length &&
                  (cond.op === "eq" || cond.op === "neq" || cond.op === "in")
                    ? [...leftField.options]
                    : undefined;
                return (
                  <PreconditionExprInput
                    expr={cond.right}
                    fields={fields}
                    numericOnly={isNumericOp}
                    onChange={(next) => updateCondition(idx, { right: next })}
                    selectOptions={rightSelectOpts}
                  />
                );
              })()}
            </div>

            {cond.op === "between" && cond.rightMax && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">
                  {t("enterpriseProductBuilder.conditionRightMax")}
                </Label>
                <PreconditionExprInput
                  expr={cond.rightMax}
                  fields={fields}
                  numericOnly
                  onChange={(next) => updateCondition(idx, { rightMax: next })}
                />
              </div>
            )}

            {cond.op === "in" && cond.rightList && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">
                  {t("enterpriseProductBuilder.conditionRightList")}
                </Label>
                {cond.rightList.map((entry, eIdx) => (
                  <div key={eIdx} className="flex items-center gap-2">
                    <PreconditionExprInput
                      expr={entry}
                      fields={fields}
                      numericOnly={false}
                      onChange={(next) => {
                        const list = [...(cond.rightList ?? [])];
                        list[eIdx] = next;
                        updateCondition(idx, { rightList: list });
                      }}
                    />
                    <Button
                      size="sm"
                      variant="ghost-destructive"
                      className="h-7"
                      onClick={() => {
                        const list = (cond.rightList ?? []).filter(
                          (_, i) => i !== eIdx,
                        );
                        updateCondition(idx, {
                          rightList: list.length > 0 ? list : [defaultRightListEntry()],
                        });
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const list = [...(cond.rightList ?? []), defaultRightListEntry()];
                    updateCondition(idx, { rightList: list });
                  }}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  {t("enterpriseProductBuilder.addValue")}
                </Button>
              </div>
            )}

            <div className="flex items-center justify-end">
              <Button
                size="sm"
                variant="ghost-destructive"
                className="h-7"
                onClick={() => removeCondition(idx)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        );
      })}

      <Button size="sm" variant="outline" onClick={addCondition}>
        <Plus className="mr-1 h-3.5 w-3.5" />
        {t("enterpriseProductBuilder.addCondition")}
      </Button>
    </div>
  );
}

function defaultRightListEntry(): PreconditionExpr {
  return { kind: "const", value: "" };
}
